import { useState, useRef } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Textarea, Label, Skeleton, Alert } from '@/components/ui'
import { Upload } from 'lucide-react'
import { api, type AppFullInfo } from '@/api/client'

interface AppContext {
  app: AppFullInfo
  setApp: (app: AppFullInfo) => void
}

export function AppSettingsPage() {
  const { orgSlug, appSlug } = useParams()
  const context = useOutletContext<AppContext | null>()
  const app = context?.app
  const setApp = context?.setApp

  const [isSaving, setIsSaving] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const iconInputRef = useRef<HTMLInputElement>(null)
  const bannerInputRef = useRef<HTMLInputElement>(null)

  // Local form state
  const [formData, setFormData] = useState<{
    name: string
    description: string
    website_url: string
    privacy_policy_url: string
    terms_of_service_url: string
  } | null>(null)

  // Initialize form data when app loads
  if (app && !formData) {
    setFormData({
      name: app.name,
      description: app.description || '',
      website_url: app.website_url || '',
      privacy_policy_url: app.privacy_policy_url || '',
      terms_of_service_url: app.terms_of_service_url || '',
    })
  }

  const handleSave = async () => {
    if (!orgSlug || !appSlug || !formData) return
    setIsSaving(true)
    try {
      const result = await api.apps.update(orgSlug, appSlug, {
        name: formData.name,
        description: formData.description || undefined,
        website_url: formData.website_url || undefined,
        privacy_policy_url: formData.privacy_policy_url || undefined,
        terms_of_service_url: formData.terms_of_service_url || undefined,
      })
      if (setApp && result.app) {
        setApp(result.app)
      }
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgSlug || !appSlug || !setApp || !app) return
    setUploadError(null)
    setIsUploadingIcon(true)
    try {
      const result = await api.apps.uploadIcon(orgSlug, appSlug, file)
      setApp({ ...app, logo_url: result.url })
    } catch (err) {
      console.error('Failed to upload icon:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload icon')
    } finally {
      setIsUploadingIcon(false)
      if (iconInputRef.current) iconInputRef.current.value = ''
    }
  }

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgSlug || !appSlug || !setApp || !app) return
    setUploadError(null)
    setIsUploadingBanner(true)
    try {
      const result = await api.apps.uploadBanner(orgSlug, appSlug, file)
      setApp({ ...app, banner_logo_url: result.url })
    } catch (err) {
      console.error('Failed to upload banner:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload banner')
    } finally {
      setIsUploadingBanner(false)
      if (bannerInputRef.current) bannerInputRef.current.value = ''
    }
  }

  if (!app || !formData) {
    return (
      <div className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {uploadError && (
        <Alert variant="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      <Card>
        <CardHeader><CardTitle>App Branding</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {/* Square Icon */}
          <div className="space-y-2">
            <Label>App Icon (Square)</Label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center overflow-hidden">
                {app.logo_url ? (
                  <img src={app.logo_url} alt={app.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-[var(--text-muted)]">{app.name?.[0] || '?'}</span>
                )}
              </div>
              <div>
                <input
                  ref={iconInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={handleIconUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => iconInputRef.current?.click()}
                  loading={isUploadingIcon}
                  disabled={isUploadingIcon}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploadingIcon ? 'Uploading...' : 'Upload Icon'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Recommended: 256x256px, PNG, JPEG, SVG, or WebP. Max 1MB.</p>
          </div>

          {/* Banner Logo */}
          <div className="space-y-2">
            <Label>Banner Logo (Wide)</Label>
            <div className="space-y-3">
              <div className="w-full h-24 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center overflow-hidden">
                {app.banner_logo_url ? (
                  <img src={app.banner_logo_url} alt={`${app.name} banner`} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-sm text-[var(--text-muted)]">No banner uploaded</span>
                )}
              </div>
              <div>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => bannerInputRef.current?.click()}
                  loading={isUploadingBanner}
                  disabled={isUploadingBanner}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isUploadingBanner ? 'Uploading...' : 'Upload Banner'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Recommended: 1200x300px, PNG, JPEG, or WebP. Max 5MB.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>App Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="App Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Input
            label="Slug"
            value={app.slug}
            disabled
            hint="Cannot be changed after creation"
          />
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="A brief description of your app"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Links</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Website URL"
            placeholder="https://example.com"
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
          />
          <Input
            label="Privacy Policy URL"
            placeholder="https://example.com/privacy"
            value={formData.privacy_policy_url}
            onChange={(e) => setFormData({ ...formData, privacy_policy_url: e.target.value })}
            hint="Shown on OAuth consent screen"
          />
          <Input
            label="Terms of Service URL"
            placeholder="https://example.com/terms"
            value={formData.terms_of_service_url}
            onChange={(e) => setFormData({ ...formData, terms_of_service_url: e.target.value })}
            hint="Shown on OAuth consent screen"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} loading={isSaving}>Save Changes</Button>
      </div>
    </div>
  )
}
